import React, { useCallback, useEffect, useRef, useState } from 'react';
import AvisoManutencao from './AvisoManutencao';
import { TiEqualsOutline } from 'react-icons/ti';
import { colorsPallete } from '../../../constants';
import { MdOutlineSendToMobile } from 'react-icons/md';
import CardLayout from '../../common/CardLayout';
import PercentProgress from '../../common/PercentProgressSmall';
import StatusOnOff from '../../common/StatusOnOff';
import apiCompressores from '../../../services/apis/apiCompressores';
import SignalService from '../../../services/SignalService';
import { apiLink } from '../../../constants/env';
import Table from '../../common/table';
import NoEquipAlert from '../../common/returns/NoEquipAlert';
import getCurrentUnidadeId from '../../../funcs/getCurrentUnidadeId';
import TrocarStatusManutencao from './TrocarStatusManutencao';
import { useMonitoramentoGeralContext } from '../../../contexts/PageConfigContext';

const ListCompressores = ({ listaRegimes, leiturasRegime }) => {
	const [listaCompressores, setListaCompressores] = useState([]);
	const [listaLeiturasCompressores, setListaLeiturasCompressores] =
		useState([]);
	const [hubConnection, setHubConnection] = useState();
	const [dadoAtualizado, setDadoAtualizado] = useState();
	const UNIDADE = getCurrentUnidadeId();
	const { setAlarmsCompressores } = useMonitoramentoGeralContext();
	const alarmStartTimesRef = useRef(new Map());

	useEffect(() => {
		const fetchData = async () => {
			try {
				const res = await apiCompressores.getByUnidadeId(UNIDADE);
				const compressores = res.data;

				const leituras = compressores.map(compressor => compressor.leiturasCompressores[0]);

				setListaCompressores(compressores);
				setListaLeiturasCompressores(leituras);
			} catch (error) {
				console.error(error.message);
			}
		}
		fetchData();
	}, [UNIDADE]);


	// ATUALIZAÇÃO EM TEMPO REAL
	useEffect(() => {
		setHubConnection(
			SignalService.startConnection(
				`${apiLink()}LeituraCompressorHub`, UNIDADE
			)
		);
	}, [UNIDADE]);

	useEffect(() => {
		if (hubConnection) {
			hubConnection
				.start()
				.then(() => {
					return hubConnection.invoke('Connect', UNIDADE);
				})
				.then(() => {
					hubConnection.on(
						'AtualizarDadosCompressor',
						(message) => {
							setDadoAtualizado(message);
						}
					);
				})
				.catch((e) => console.log('Conexão falhou. ', e));
		}
	}, [hubConnection, UNIDADE]);

	// ATUALIZAR DADOS
	useEffect(() => {
		if (dadoAtualizado) {
			const index = listaLeiturasCompressores.findIndex(
				(elemento) =>
					elemento.compressorId ===
					dadoAtualizado.compressorId
			);

			let arrayAux = [...listaLeiturasCompressores];
			if (index === -1) {
				arrayAux.push(dadoAtualizado);
			} else {
				arrayAux[index] = dadoAtualizado;
			}
			setListaLeiturasCompressores([...arrayAux]);
			setDadoAtualizado(null);
		}
	}, [dadoAtualizado, listaLeiturasCompressores]);

	const getLeituraByRegime = useCallback((id) => {
		return leiturasRegime.find((elemento) => elemento.regimeId === id);
	}, [leiturasRegime]);

	const getLeituraByCompressor = useCallback((id) => {
		const leitura = listaLeiturasCompressores.find(
			(elemento) => elemento?.compressorId === id
		);

		if (leitura.dataHora === "0001-01-01T00:00:00") {
			leitura.ligado = false;
		}

		return leitura;
	}, [listaLeiturasCompressores]);

	const getListaCompressoresByRegimeId = useCallback((id) => {
		return listaCompressores.filter(
			(elemento) => elemento?.regimeSuccaoId === id)
	}, [listaCompressores]);

	useEffect(() => {
		const now = new Date();
		const startTimes = alarmStartTimesRef.current;
		const activeAlarms = [];

		listaCompressores.forEach((compressor) => {
			const leitura = listaLeiturasCompressores.find(l => l?.compressorId === compressor.id);
			if (!leitura || !leitura.ligado || leitura.manutencao) return;

			const checks = [
				{
					key: `${compressor.id}_capacidade`,
					condition: leitura.porcentagemCapacidade < 50,
					descricao: 'Capacidade < 50%',
					valor: leitura.porcentagemCapacidade,
				},
				{
					key: `${compressor.id}_tempMax`,
					condition: !!compressor.setPointTemperaturaDescargaMaxima &&
						leitura.temperaturaDescarga > compressor.setPointTemperaturaDescargaMaxima,
					descricao: 'Temp. descarga acima do máximo',
					valor: leitura.temperaturaDescarga,
				},
				{
					key: `${compressor.id}_tempMin`,
					condition: !!compressor.setPointTemperaturaDescargaMinima &&
						leitura.temperaturaDescarga < compressor.setPointTemperaturaDescargaMinima,
					descricao: 'Temp. descarga abaixo do mínimo',
					valor: leitura.temperaturaDescarga,
				},
			];

			checks.forEach(({ key, condition, descricao, valor }) => {
				if (condition) {
					if (!startTimes.has(key)) startTimes.set(key, now);
					activeAlarms.push({
						equipamentoNome: compressor.nome,
						descricao,
						valor,
						dataHoraInicio: startTimes.get(key),
					});
				} else {
					startTimes.delete(key);
				}
			});
		});

		setAlarmsCompressores(activeAlarms);
	}, [listaLeiturasCompressores, listaCompressores, setAlarmsCompressores]);

	function isCompressorAlarmed(compressor) {
		const leitura = getLeituraByCompressor(compressor.id);
		if (!leitura || !leitura.ligado || leitura.manutencao) return false;
		if (leitura.porcentagemCapacidade < 50) return true;
		if (compressor.setPointTemperaturaDescargaMaxima &&
			leitura.temperaturaDescarga > compressor.setPointTemperaturaDescargaMaxima) return true;
		if (compressor.setPointTemperaturaDescargaMinima &&
			leitura.temperaturaDescarga < compressor.setPointTemperaturaDescargaMinima) return true;
		return false;
	}

	return (
		<>
			<div className="flex flex-col w-full gap-4 items-center">
				{listaRegimes?.map((regime, i) => (
					<CardLayout key={i} className="mx-16">
						<div className="flex justify-center align-items-center flex-col">
							<div className="flex flex-row justify-center items-center">
								<div
									className={`text-left md:text-center px-4 py-2 text-lg md:text-2xl font-semibold ${colorsPallete[i].class}`}
								>
									{`REGIME ${regime.nome}`}
									<p className="text-sm md:text-lg font-semibold lg:px-8 block lg:inline-block text-accent drop-shadow-1xl">
										{'Capacidade '}
										<TiEqualsOutline className="inline" />
										<span className="text-white px-1">
											{getLeituraByRegime(
												regime.id
											)?.capacidadeCompressores?.toLocaleString(
												'pt-BR',
												{
													maximumFractionDigits: 2,
												}
											) || 0}{' '}
											Kcal/ h
										</span>
									</p>
									<p className="text-sm md:text-lg font-semibold block lg:inline-block text-accent drop-shadow-1xl">
										{'Potência '}
										<TiEqualsOutline className="inline" />
										<span className="text-white px-1">
											{getLeituraByRegime(
												regime.id
											)?.potenciaCompressores?.toLocaleString(
												'pt-BR',
												{
													maximumFractionDigits: 2,
												}
											) || 0}{' '}
											kW
										</span>
									</p>
									<p className="text-sm md:text-lg font-semibold lg:px-8 block lg:inline-block text-accent drop-shadow-1xl">
										{'I.E.E. '}
										<TiEqualsOutline className="inline" />
										<span className="text-white px-1">
											{getLeituraByRegime(
												regime.id
											)?.ieeCompressores?.toLocaleString(
												'pt-BR',
												{
													maximumFractionDigits: 2,
												}
											) || 0}{' '}
											Kcal/ h
										</span>
									</p>
								</div>
								<div className="md:hidden flex justify-center items-center text-accent text-2xl">
									<MdOutlineSendToMobile />
								</div>
							</div>

							<Table.Root>
								<Table.Head>
									<Table.Header>
										Status
									</Table.Header>
									<Table.Header>
										TAG
									</Table.Header>
									<Table.Header>
										Modelo
									</Table.Header>
									<Table.Header>
										Slide
									</Table.Header>

									<Table.Header span={3}>
										<div className="border-[1px] border-accent/40 rounded">
											Pressão
										</div>
									</Table.Header>
									<Table.Header span={2}>
										<div className="border-[1px] border-accent/40 rounded">
											Temperatura
										</div>
									</Table.Header>
									<Table.Header span={2}>
										<div className="border-[1px] border-accent/40 rounded">
											Óleo
										</div>
									</Table.Header>
									<Table.Header>
										Corrente
									</Table.Header>
									<Table.Header>
										Freq.
									</Table.Header>
									<Table.Header>
										Potência
									</Table.Header>
									<Table.Header>
										Eficiência
									</Table.Header>
									<Table.Header>
										I.E.E.
									</Table.Header>
									{/* <Table.Header>
										Capacidade
									</Table.Header> */}
								</Table.Head>
								<Table.Head>
									<Table.Header
										islower={true}
										span={4}
									></Table.Header>
									<Table.Header
										islower={true}
									>
										Setup
									</Table.Header>
									<Table.Header
										islower={true}
									>
										Sucção
									</Table.Header>
									<Table.Header
										islower={true}
									>
										Descarga
									</Table.Header>
									<Table.Header
										islower={true}
									>
										Sucção
									</Table.Header>
									<Table.Header
										islower={true}
									>
										Descarga
									</Table.Header>
									<Table.Header
										islower={true}
									>
										Pressão
									</Table.Header>
									<Table.Header
										islower={true}
									>
										Temperatura
									</Table.Header>
									<Table.Header
										islower={true}
										span={6}
									></Table.Header>
								</Table.Head>

								<Table.Body>
									{getListaCompressoresByRegimeId(
										regime.id
									)?.length > 0 ? (
										getListaCompressoresByRegimeId(
											regime.id
										)?.map(
											(
												compressor,
												i
											) =>
												Object.keys(
													compressor
												)
													.length >
													1 ? (
													<Table.Row
														key={i}
														className={isCompressorAlarmed(compressor) ? 'animate-warning' : ''}
													>
														<Table.Data>
															<TrocarStatusManutencao compressor={compressor} manutencao={getLeituraByCompressor(
																compressor.id
															)
																?.manutencao}>
																<StatusOnOff
																	status={
																		getLeituraByCompressor(
																			compressor.id
																		)
																			?.ligado
																	}

																	manutencao={getLeituraByCompressor(
																		compressor.id
																	)
																		?.manutencao}
																/>
															</TrocarStatusManutencao>
														</Table.Data>
														<Table.Data>
															{compressor?.nome}
															<AvisoManutencao tipo="Compressor" nome={compressor.nome} />
														</Table.Data>
														<Table.Data>
															{compressor?.modeloCompressor?.nome}
														</Table.Data>
														<Table.Data>
															<PercentProgress
																value={
																	getLeituraByCompressor(
																		compressor.id
																	)?.slide.toLocaleString(
																		'pt-BR',
																		{
																			maximumFractionDigits: 1,
																		}
																	) ||
																	0
																}
															/>
														</Table.Data>
														<Table.Data>
															{getLeituraByCompressor(
																compressor.id
															)?.pressaoSetup?.toLocaleString(
																'pt-BR',
																{
																	maximumFractionDigits: 2,
																}
															) ||
																'-'}{' '}
															Bar
														</Table.Data>
														<Table.Data>

															{getLeituraByCompressor(
																compressor.id
															)?.pressaoSuccao?.toLocaleString(
																'pt-BR',
																{
																	maximumFractionDigits: 2,
																}
															) ||
																'-'}{' '}
															Bar
														</Table.Data>
														<Table.Data>
															{getLeituraByCompressor(
																compressor.id
															)?.pressaoDescarga?.toLocaleString(
																'pt-BR',
																{
																	maximumFractionDigits: 1,
																}
															) ||
																'-'}{' '}
															Bar
														</Table.Data>
														<Table.Data>
															{getLeituraByCompressor(
																compressor.id
															)?.temperaturaSuccao?.toLocaleString(
																'pt-BR',
																{
																	maximumFractionDigits: 1,
																}
															) ||
																'-'}{' '}
															°C
														</Table.Data>
														<Table.Data>
															{getLeituraByCompressor(
																compressor.id
															)?.temperaturaDescarga?.toLocaleString(
																'pt-BR',
																{
																	maximumFractionDigits: 1,
																}
															) ||
																'-'}{' '}
															°C
														</Table.Data>
														<Table.Data>
															{getLeituraByCompressor(
																compressor.id
															)?.pressaoOleoReservatorio?.toLocaleString(
																'pt-BR',
																{
																	maximumFractionDigits: 1,
																}
															) ||
																'-'}{' '}
															Bar
														</Table.Data>
														<Table.Data>
															{getLeituraByCompressor(
																compressor.id
															)?.temperaturaOleo?.toLocaleString(
																'pt-BR',
																{
																	maximumFractionDigits: 1,
																}
															) ||
																'-'}{' '}
															°C
														</Table.Data>
														<Table.Data>
															{getLeituraByCompressor(
																compressor.id
															)?.corrente?.toLocaleString(
																'pt-BR',
																{
																	maximumFractionDigits: 1,
																}
															) ||
																'-'}{' '}
															A
														</Table.Data>
														<Table.Data>
															{getLeituraByCompressor(
																compressor.id
															)?.frequencia?.toLocaleString(
																'pt-BR',
																{
																	maximumFractionDigits: 1,
																}
															) ||
																'-'}{' '}
															Hz
														</Table.Data>
														<Table.Data>
															{getLeituraByCompressor(
																compressor.id
															)?.potenciaKw?.toLocaleString(
																'pt-BR',
																{
																	maximumFractionDigits: 1,
																}
															) ||
																'-'}{' '}
															kW
														</Table.Data>
														<Table.Data>
															{getLeituraByCompressor(
																compressor.id
															)?.eficiencia?.toLocaleString(
																'pt-BR',
																{
																	maximumFractionDigits: 1,
																}
															) ||
																'-'}{' '}
															%
														</Table.Data>
														<Table.Data>
															{getLeituraByCompressor(
																compressor.id
															)?.iee?.toLocaleString(
																'pt-BR',
																{
																	maximumFractionDigits: 1,
																}
															) ||
																'-'}{' '}
															Kcal/kWh
														</Table.Data>
														{/* <Table.Data>
															{getLeituraByCompressor(
																compressor.id
															)?.capacidadeCalculada?.toLocaleString(
																'pt-BR',
																{
																	maximumFractionDigits: 2,
																}
															) ||
																'-'}{' '}
															Kcal/
															h
														</Table.Data> */}
													</Table.Row>
												) : null
										)
									) : (
										<Table.Row>
											<Table.Data
												className="text-center"
												span={
													15
												}
											>
												<NoEquipAlert
													equipments={
														'compressores'
													}
													regime={
														true
													}
												/>
											</Table.Data>
										</Table.Row>
									)}
								</Table.Body>
							</Table.Root>
						</div>
					</CardLayout>
				))}
			</div>
		</>
	);
};

export default ListCompressores;

